import type {
  CloneIneligibleReason,
  CloneStepIscoreInfo,
  CloneStepPreview,
  JourneyCase,
  StepCloneOutcome,
} from "../dtos";

/**
 * §Clone-steps: pure helpers behind `clone-steps-dialog.tsx` — kept dependency-free (no React, no
 * network) so the picker's rules (labels, grouping, default selection, overwrite detection, outcome
 * copy) are unit-testable without mounting anything.
 */

/** "Application N" — MUST be called with `cases` already oldest-first (`sortJourneyCases`'s order,
 * the same one `CaseSwitcher` numbers by), so the label a dialog shows for a source always matches
 * the chip the agent already knows it by. */
export const applicationLabel = (cases: readonly JourneyCase[], caseUid: string): string => {
  const index = cases.findIndex((row) => row.uid === caseUid);
  return index >= 0 ? `Application ${index + 1}` : "Application";
};

/** Every OTHER application on the lead — the source picker's candidate list when copying into an
 * application that already exists (a case is never its own clone source). */
export const otherApplications = (
  cases: readonly JourneyCase[],
  excludeCaseUid: string | null
): JourneyCase[] => cases.filter((row) => row.uid !== excludeCaseUid);

export interface ClonePreviewPhaseGroup {
  phaseName: string;
  rows: CloneStepPreview[];
}

/** Group preview rows under their phase, preserving the phases' first-appearance (server) order —
 * the checklist renders one heading per phase, not a re-sorted alphabetical list. */
export const groupPreviewByPhase = (
  rows: readonly CloneStepPreview[]
): ClonePreviewPhaseGroup[] => {
  const groups: ClonePreviewPhaseGroup[] = [];
  const byPhase = new Map<string, ClonePreviewPhaseGroup>();
  for (const row of rows) {
    let group = byPhase.get(row.phaseName);
    if (!group) {
      group = { phaseName: row.phaseName, rows: [] };
      byPhase.set(row.phaseName, group);
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
};

/** Every eligible step, ticked by default — the dialog seeds its checkbox state with this each time
 * a new preview loads (a fresh source pick, or the initial load). */
export const defaultSelectedTemplateStepIds = (
  rows: readonly CloneStepPreview[]
): Set<number> => new Set(rows.filter((row) => row.eligible).map((row) => row.templateStepId));

/** True once at least one TICKED row already carries data on the target — the point at which the
 * dialog must show the "Overwrites existing data" band and gate Confirm behind an explicit checkbox. */
export const needsOverwriteConfirm = (
  rows: readonly CloneStepPreview[],
  selected: ReadonlySet<number>
): boolean => rows.some((row) => selected.has(row.templateStepId) && row.targetHasData);

const INELIGIBLE_REASON_COPY: Record<CloneIneligibleReason, string> = {
  TARGET_COMPLETED: "Already completed — reopen it first",
  TARGET_SKIPPED: "Skipped on this application",
  TARGET_MISSING: "Not on this application",
  SOURCE_MISSING: "Not on the source application",
  NO_SOURCE_DATA: "Nothing to copy",
  NO_SOURCE_ACCESS: "You can't view this step on the source",
  NOT_A_WRITER: "You can't edit this step",
  HELD_BY_OTHER: "Held by another agent — take it over first",
  CLONE_IN_PROGRESS: "Being copied right now",
};

/** Short copy for a disabled row's reason chip. Null in, null out — an eligible row names nothing. */
export const ineligibleReasonCopy = (reason: CloneIneligibleReason | null): string | null =>
  reason ? INELIGIBLE_REASON_COPY[reason] : null;

/** `YYYY-MM-DD` → `DD/MM/YYYY`, string-sliced rather than parsed through `Date` so the calendar day
 * the server meant never shifts a day under the browser's timezone. Malformed input passes through
 * unchanged rather than throwing — this only ever feeds a caption, never a computation. */
export const formatDdMmYyyy = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : isoDate;
};

export interface CloneIscoreNote {
  text: string;
  /** true ⇒ render as a warning (expired, will buy a fresh report) rather than plain caption copy */
  warn: boolean;
}

/** §Clone-steps decision 8: the i-Score row's note — a valid stored report costs no bureau call;
 * an expired one still gets bought fresh on submit, same as today, so the dialog says so up front. */
export const iscoreNoteFor = (iscore: CloneStepIscoreInfo | null): CloneIscoreNote | null => {
  if (!iscore) return null;
  if (iscore.willPurchase) {
    return { text: "Stored i-Score expired — submitting will buy a new report", warn: true };
  }
  if (!iscore.validUntil) return null;
  return {
    text: `Stored i-Score valid until ${formatDdMmYyyy(iscore.validUntil)} — no new bureau request`,
    warn: false,
  };
};

/** The `cloneFrom` block for `POST customers/:uid/workflow-cases` — omitted (undefined, never an
 * empty object) unless the agent chose to copy AND actually ticked at least one step, so an
 * unticked "Copy from…" pick still starts a perfectly ordinary empty application. */
export const cloneFromForStart = (
  copying: boolean,
  sourceCaseUid: string | null,
  selected: ReadonlySet<number>
): { sourceCaseUid: string; templateStepIds: number[] } | undefined => {
  if (!copying || !sourceCaseUid || selected.size === 0) return undefined;
  return { sourceCaseUid, templateStepIds: [...selected].sort((a, b) => a - b) };
};

/** Per-outcome summary line after `POST /v1/cases/:uid/step-clones` resolves. */
export const outcomeLabel = (outcome: StepCloneOutcome): string => {
  switch (outcome.status) {
    case "APPLIED":
      return "Filled now";
    case "PENDING":
      // A job that already failed once is waiting to retry — not waiting for the step to open.
      return outcome.reason === "RETRY_SCHEDULED"
        ? `Copy failed — retrying automatically (${outcome.message ?? "error"})`
        : "Will fill when the step opens";
    case "CONFLICT":
      return outcome.message ?? "Skipped — the step changed after this was planned";
    case "BLOCKED":
      return outcome.message ?? "Blocked";
    case "FAILED":
      return outcome.message ?? "Failed";
    case "SUPERSEDED":
      return "Replaced by a newer copy request";
    case "EXPIRED":
      return "Expired before the step opened";
    default:
      return outcome.message ?? outcome.status;
  }
};

/** Read a workflow-manager error body's `code` (`CLONE_OVERWRITE_REQUIRED`, `CLONE_IN_PROGRESS`,
 * `CLONE_SOURCE_NOT_SAME_LEAD`, `CLONE_SOURCE_OTHER_CUSTOMER`, …) without pulling in axios — the
 * shape is whatever `wmRequest` rejects with, which always carries `response.data` on an HTTP error. */
export const cloneErrorCode = (error: unknown): string | null => {
  const body = (error as { response?: { data?: { code?: unknown } } } | undefined)?.response?.data;
  return typeof body?.code === "string" ? body.code : null;
};

/** The 409 that means "this would overwrite existing data — confirm and resend with `overwrite: true`". */
export const isCloneOverwriteRequired = (error: unknown): boolean =>
  cloneErrorCode(error) === "CLONE_OVERWRITE_REQUIRED";
